import { put, takeEvery, all } from "redux-saga/effects";
import {
  getPaginatedNotifications,
  setAllNotificationAsRead,
  setNotificationRead
} from "../../services/database";
import { isValidObject } from "../../utils/validators";
import { setErrorStatus } from "../status/actions";
import store from "../store/store";
import { updateNotifications } from "./actions";

export const notificationActionTypes = {
  SET_NOTIFICATION: "SET_NOTIFICATION",
  UPDATE_NOTIFICATION: "UPDATE_NOTIFICATION",
  SET_NOTIFICATION_READ: "SET_NOTIFICATION_READ"
};

// categories whose notifications mean the consent list changed
const CONSENT_LIST_CATEGORIES = ["CONSENT", "SUBSCRIPTION", "LOCKER"];

// true when the snapshot brings a consent-list notification for the current
// profile that wasn't there before (skips the first load of a session)
const hasNewConsentListNotification = (existingData, incomingData) => {
  const { currentProfile, accessToken } = store.getState().patients;
  const existing = existingData?.[currentProfile];
  const incoming = incomingData?.[currentProfile];
  if (
    !accessToken?.[currentProfile]?.token ||
    !isValidObject(existing) ||
    !isValidObject(incoming)
  ) {
    return false;
  }
  return Object.entries(incoming).some(
    ([docId, notification]) =>
      !existing[docId] &&
      CONSENT_LIST_CATEGORIES.includes(notification?.category)
  );
};

function* setNotificationWorker(action) {
  try {
    setNotificationLoading(true);

    const state = store.getState();
    const existingData = state.notifications.data;

    if (hasNewConsentListNotification(existingData, action.payload.data)) {
      yield put({
        type: "GET_PATIENTS_REQUESTS",
        payload: { silent: true }
      });
    }

    yield put({
      type: "SET_NOTIFICATION_DATA",
      payload: {
        data: isValidObject(existingData)
          ? { ...existingData, ...action.payload.data }
          : action.payload.data
      }
    });

    // const existingPagination = state.notifications.pagination;
    // yield put({
    //   type: "SET_NOTIFICATION_PAGINATION",
    //   payload: {
    //     data: isValidObject(existingPagination)
    //       ? {
    //           ...existingPagination,
    //           [action.payload.profileId]: {
    //             0: action.payload.lastVisible
    //           }
    //         }
    //       : {
    //           [action.payload.profileId]: {
    //             0: action.payload.lastVisible
    //           }
    //         }
    //   }
    // });

    setNotificationLoading(false);
  } catch (error) {
    setErrorStatus(error);
    setNotificationLoading(false);
  }
}

function* updateNotificationWorker(action) {
  try {
    yield setNotificationPaginationLoading(true);

    const { patients, notifications } = store.getState();
    const profileId = patients.currentProfile;
    const existingData = notifications.data?.[profileId] || {};
    const existingPagination = notifications.pagination?.[profileId] || {};

    // Determine last document page number
    const lastDocumentPageNumber =
      action.payload?.lastDocPageNumber ??
      Object.values(existingData)?.[Object.values(existingData).length - 1]
        ?.pageNumber;

    if (!profileId || lastDocumentPageNumber === undefined) {
      yield setNotificationPaginationLoading(false);
      return;
    }

    const paginationData = action.payload?.lastDocPageNumber
      ? Object.values(existingPagination)[action.payload.lastDocPageNumber - 1]
      : Object.values(existingPagination)[lastDocumentPageNumber];

    if (!paginationData) {
      yield setNotificationPaginationLoading(false);
      return;
    }

    const paginationNotification = yield getPaginatedNotifications(
      profileId,
      paginationData,
      lastDocumentPageNumber,
      !!action.payload?.lastDocPageNumber
    );

    if (paginationNotification.notifications) {
      yield put({
        type: "SET_NOTIFICATION_DATA",
        payload: {
          data: {
            ...notifications.data,
            [profileId]: {
              ...existingData,
              ...paginationNotification.notifications
            }
          }
        }
      });
    }

    if (
      !action.payload?.lastDocPageNumber &&
      paginationNotification.lastVisible
    ) {
      yield put({
        type: "SET_NOTIFICATION_PAGINATION",
        payload: {
          data: {
            ...notifications.pagination,
            [profileId]: {
              ...existingPagination,
              [lastDocumentPageNumber + 1]: paginationNotification.lastVisible
            }
          }
        }
      });
    }

    yield setNotificationPaginationLoading(false);
  } catch (error) {
    setErrorStatus(error);
    yield setNotificationPaginationLoading(false);
  }
}

function* setNotificationReadWorker(action) {
  try {
    const { patients, notifications } = store.getState();
    const currentProfile = patients.currentProfile;

    if (!currentProfile) return;

    if (action.payload.type === "all") {
      const notificationsList = Object.values(
        notifications.data[currentProfile] || {}
      ).filter((data) => data.read === false);

      if (notificationsList.length > 0) {
        const response = yield setAllNotificationAsRead(notificationsList);
        if (response?.success) {
          yield updateNotifications(1);
        }
      }
    } else {
      const { documentId, pageNumber } = action.payload.notifications;

      if (!documentId) return;

      const response = yield setNotificationRead(documentId, currentProfile);
      if (response?.success) {
        yield updateNotifications(pageNumber);
      }
    }
  } catch (error) {
    setErrorStatus(error);
  }
}

export default function* notificationsWatcher() {
  yield all([
    takeEvery("SET_NOTIFICATION", setNotificationWorker),
    takeEvery("UPDATE_NOTIFICATION", updateNotificationWorker),
    takeEvery("SET_NOTIFICATION_READ", setNotificationReadWorker)
  ]);
}

function* setNotificationLoading(value) {
  yield put({
    type: "SET_NOTIFICATION_LOADING",
    payload: {
      loading: value
    }
  });
}

function* setNotificationPaginationLoading(value) {
  yield put({
    type: "SET_NOTIFICATION_PAGINATION_LOADING",
    payload: {
      loading: value
    }
  });
}
